import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  api,
  getAdminToken,
  clearAdminToken,
  authHeaders,
} from "@/lib/api";
import { LogOut, RefreshCcw, Trash2, Mail } from "lucide-react";

const STATUS_OPTIONS = ["new", "contacted", "closed"];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/inquiries", {
        headers: authHeaders(),
      });
      setItems(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        clearAdminToken();
        navigate("/admin");
      } else {
        toast.error("Failed to load inquiries");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!getAdminToken()) {
      navigate("/admin");
      return;
    }
    fetchAll();
  }, []);

  const logout = async () => {
    try {
      await api.post("/admin/logout", {}, { headers: authHeaders() });
    } catch (_) {
      // ignore
    }
    clearAdminToken();
    navigate("/admin");
  };

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(
        `/admin/inquiries/${id}`,
        { status },
        { headers: authHeaders() }
      );
      setItems((prev) => prev.map((it) => (it.id === id ? data : it)));
      if (selected?.id === id) setSelected(data);
      toast.success(`Marked ${status}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try {
      await api.delete(`/admin/inquiries/${id}`, { headers: authHeaders() });
      setItems((prev) => prev.filter((it) => it.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((i) => i.status === filter);
  }, [items, filter]);

  const stats = useMemo(() => {
    const s = { total: items.length, new: 0, contacted: 0, closed: 0 };
    items.forEach((i) => {
      s[i.status] = (s[i.status] || 0) + 1;
    });
    return s;
  }, [items]);

  return (
    <div data-testid="page-admin-dashboard" className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <p className="label-eyebrow">/ Admin / Inquiries</p>
          <h1 className="font-heading mt-3 text-3xl md:text-5xl font-black uppercase tracking-tighter">
            Control Room
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="admin-refresh"
            onClick={fetchAll}
            className="px-4 py-2.5 border border-zinc-800 text-xs uppercase tracking-widest text-zinc-300 hover:border-white inline-flex items-center gap-2"
          >
            <RefreshCcw size={14} /> Refresh
          </button>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="px-4 py-2.5 border border-zinc-800 text-xs uppercase tracking-widest text-zinc-300 hover:border-white inline-flex items-center gap-2"
          >
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 border border-zinc-800">
        {[
          ["Total", stats.total],
          ["New", stats.new || 0],
          ["Contacted", stats.contacted || 0],
          ["Closed", stats.closed || 0],
        ].map(([k, v], i) => (
          <div
            key={k}
            data-testid={`admin-stat-${k.toLowerCase()}`}
            className={`p-6 ${i < 3 ? "md:border-r border-zinc-800" : ""} ${
              i < 2 ? "border-b md:border-b-0 border-zinc-800 border-r md:border-r" : ""
            }`}
          >
            <p className="label-eyebrow text-zinc-500">{k}</p>
            <p className="mt-3 font-heading text-3xl font-black copper-text">
              {v}
            </p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="mt-8 flex flex-wrap gap-2">
        {["all", "new", "contacted", "closed"].map((s) => (
          <button
            key={s}
            data-testid={`admin-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${
              filter === s
                ? "copper-bg text-black border-transparent"
                : "border-zinc-800 text-zinc-300 hover:border-white"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Grid */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 border border-zinc-800 overflow-hidden">
          <div className="bg-zinc-900 px-5 py-3 border-b border-zinc-800 flex items-center justify-between">
            <span className="label-eyebrow text-zinc-300">
              Inquiries ({filtered.length})
            </span>
          </div>
          <div className="max-h-[640px] overflow-auto">
            {loading ? (
              <div className="p-10 text-center text-zinc-500 text-sm">
                Loading...
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-10 text-center text-zinc-500 text-sm" data-testid="admin-empty">
                No inquiries yet.
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="admin-inquiry-table">
                <thead className="bg-zinc-900 text-zinc-400">
                  <tr>
                    <th className="text-left px-4 py-3 label-eyebrow">Name</th>
                    <th className="text-left px-4 py-3 label-eyebrow">Company</th>
                    <th className="text-left px-4 py-3 label-eyebrow">MT</th>
                    <th className="text-left px-4 py-3 label-eyebrow">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((it) => (
                    <tr
                      key={it.id}
                      data-testid={`admin-row-${it.id}`}
                      onClick={() => setSelected(it)}
                      className={`border-b border-zinc-800 hover:bg-zinc-900 cursor-pointer ${
                        selected?.id === it.id ? "bg-zinc-900" : ""
                      }`}
                    >
                      <td className="px-4 py-3 text-zinc-200">{it.name}</td>
                      <td className="px-4 py-3 text-zinc-400">
                        {it.company || "—"}
                      </td>
                      <td className="px-4 py-3 text-zinc-400">
                        {it.quantity_mt ?? "—"}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={it.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="lg:col-span-5 border border-zinc-800 bg-[var(--eel-surface)] min-h-[400px]">
          {selected ? (
            <div className="p-6" data-testid="admin-detail">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="label-eyebrow text-zinc-500">Inquiry</p>
                  <h3 className="font-heading text-xl font-bold mt-1">
                    {selected.name}
                  </h3>
                  {selected.company && (
                    <p className="text-sm text-zinc-400">{selected.company}</p>
                  )}
                </div>
                <StatusBadge status={selected.status} />
              </div>

              <dl className="mt-6 space-y-3 text-sm">
                <Row k="Email">
                  <a
                    href={`mailto:${selected.email}`}
                    className="text-zinc-200 hover:text-[var(--eel-copper)] inline-flex items-center gap-2"
                  >
                    <Mail size={12} /> {selected.email}
                  </a>
                </Row>
                <Row k="Phone">{selected.phone || "—"}</Row>
                <Row k="Country">{selected.country || "—"}</Row>
                <Row k="Quantity (MT)">{selected.quantity_mt ?? "—"}</Row>
                <Row k="Submitted">
                  {new Date(selected.created_at).toLocaleString()}
                </Row>
              </dl>

              <div className="mt-5">
                <p className="label-eyebrow text-zinc-500">Message</p>
                <p className="mt-2 text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">
                  {selected.message}
                </p>
              </div>

              <div className="mt-8 border-t border-zinc-800 pt-5">
                <p className="label-eyebrow text-zinc-500 mb-3">Update Status</p>
                <div className="flex flex-wrap gap-2">
                  {STATUS_OPTIONS.map((s) => (
                    <button
                      key={s}
                      data-testid={`admin-detail-status-${s}`}
                      onClick={() => updateStatus(selected.id, s)}
                      disabled={selected.status === s}
                      className={`px-3 py-2 text-xs uppercase tracking-widest border ${
                        selected.status === s
                          ? "copper-bg text-black border-transparent"
                          : "border-zinc-800 text-zinc-300 hover:border-white"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                  <button
                    data-testid="admin-detail-delete"
                    onClick={() => remove(selected.id)}
                    className="ml-auto px-3 py-2 text-xs uppercase tracking-widest border border-red-900 text-red-400 hover:bg-red-950 inline-flex items-center gap-2"
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full grid place-items-center p-10 text-center text-zinc-500 text-sm">
              Select an inquiry to view details.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ k, children }) {
  return (
    <div className="flex items-baseline gap-4">
      <dt className="label-eyebrow text-zinc-500 w-28 shrink-0">{k}</dt>
      <dd className="text-zinc-300">{children}</dd>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    new: "border-[var(--eel-copper)] text-[var(--eel-copper)]",
    contacted: "border-zinc-400 text-zinc-200",
    closed: "border-zinc-700 text-zinc-500",
  };
  return (
    <span
      className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${
        map[status] || "border-zinc-700 text-zinc-400"
      }`}
    >
      {status}
    </span>
  );
}
